import _, { delay } from "lodash"
import { PageId } from "../common/pageId"
import {
  IFieldValues,
  pushHistory,
  updateFlightSelection
} from "../containers/selectionSlice"
import * as Utils from "../components/Utils"
import {
  deleteAllTransactions,
  deleteTransactions,
  getFlightAvailability,
  getFlightCountReport,
  getFlightReports,
  getFlights,
  getSlotAvailability,
  getTransactions,
  makeAddTransactions,
  makeEditTransaction,
  refreshFlights,
  saveFlightFilter,
  saveTransactions,
  sendTransactions
} from "../api/BackendCalls"
import {
  getAppFileVersion,
  OnFailure,
  JsonErrorCode
} from "../api/Communication"
import { getPageConfig } from "./Config"
import { FieldName } from "../common/text"
import { Flights } from "../api/Flight"
import { Transactions } from "../api/Transaction"
import { ActionType } from "../components/ResponsePane"
import store from "../app/Store"
import { logoutUser } from "../containers/applicationSlice"
import {
  closeAlmostAllNotifications,
  INotification,
  NotificationKind,
  notify
} from "../containers/notificationSlice"
import { FlightReports } from "../api/FlightReport"

function saveSelectionHistory(dispatch, parameters: IFieldValues) {
  // Save selection in the store BEFORE query execution
  // (Kim's request)

  if (parameters.lastUpdate) {
    let d = new Date(parameters.lastUpdate)
    parameters.lastUpdate = Utils.formatDate(d)
  }
  _.each(_.without(_.keys(parameters), "viewRestricted"), (key) => {
    dispatch(
      updateFlightSelection({
        field: FieldName[key],
        value: parameters[key]
      })
    )
  })
  dispatch(
    pushHistory({
      page: PageId.All,
      values: _.pickBy(parameters, (v, k) => k !== "season")
    })
  )
}

export function SubmitSelection(
  dispatch,
  parameters: IFieldValues,
  pageId: string,
  onSuccess?: () => void,
  onFail?: () => void
) {
  const pageConfig = getPageConfig[pageId]
  let _parameters = _.clone(parameters)
  let selectorParameters: IFieldValues = {}
  Object.keys(_parameters).forEach((p) => {
    if (
      (pageConfig.flightSelectorConfig &&
        !pageConfig.flightSelectorConfig[p]) ||
      (p === FieldName.endDate && pageId === PageId.EditFlightsSD)
    ) {
      selectorParameters[p] = _parameters[p]
    }
  })

  saveSelectionHistory(dispatch, selectorParameters)
  if (
    selectorParameters.serviceNumber !== undefined &&
    selectorParameters.serviceNumberTo !== undefined
  ) {
    if (
      selectorParameters.serviceNumber.length > 0 &&
      selectorParameters.serviceNumberTo.length > 0
    ) {
      selectorParameters.serviceNumber =
        selectorParameters.serviceNumber + "-" + _parameters.serviceNumberTo
    }
    if (
      selectorParameters.serviceNumber.length === 0 &&
      selectorParameters.serviceNumberTo.length > 0
    ) {
      selectorParameters.serviceNumber = _parameters.serviceNumberTo
    }
    delete selectorParameters.serviceNumberTo
  }

  saveFlightFilter(pageId, selectorParameters).then((r) => {
    if (r) {
      if (typeof onSuccess === "function") {
        onSuccess()
      }
    } else {
      if (typeof onFail === "function") {
        onFail()
      }
    }
  })
}

export function handleRemoveNotifications() {
  store.dispatch(closeAlmostAllNotifications())
}

export function handleSubmit() {
  handleRemoveNotifications()
}

export function handleFailure(errorCode: JsonErrorCode) {
  // if error is authN or authZ, set user state to logged out
  if (errorCode === JsonErrorCode.AuthenticationError) {
    store.dispatch(logoutUser({}))
    getAppFileVersion().then((svrVersion) => {
      const myVersion = process.env.REACT_APP_VERSION
      if (svrVersion && svrVersion !== myVersion) {
        const n: INotification = {
          kind: NotificationKind.WARNING,
          title: undefined, // ignored
          message: "Loading new version of OCS ..."
        }
        store.dispatch(notify(n))
        delay(() => {
          window.location.reload() // reload of app after 5 secs
        }, 5000)
      }
    })
  }
}

//#region START functions for Transactions
export function handleMakeEditTransactions(
  pageId: PageId,
  parameters: any,
  delink: boolean,
  useLocalTime: boolean,
  normalizePeriod: boolean,
  onSuccess: (
    flights: Flights,
    transList: Transactions,
    action?: ActionType
  ) => void,
  onFailure: () => void
) {
  handleSubmit()
  makeEditTransaction(
    pageId,
    parameters,
    () => {
      handleRefreshFlightsAndTransactions(
        pageId,
        delink,
        useLocalTime,
        normalizePeriod,
        onSuccess,
        onFailure
      )
    },
    onFailure
  )
}

export function handleDeleteTransactions(
  pageId: PageId,
  parameters: any,
  delink: boolean,
  useLocalTime: boolean,
  normalizePeriod: boolean,
  onSuccess: (flights: Flights, transList: Transactions) => void,
  onFailure: OnFailure
) {
  handleSubmit()
  deleteTransactions(
    pageId,
    parameters,
    () => {
      handleRefreshFlightsAndTransactions(
        pageId,
        delink,
        useLocalTime,
        normalizePeriod,
        onSuccess,
        onFailure
      )
    },
    onFailure
  )
}

export function handleDeleteAllTransactions(pageId: PageId) {
  handleSubmit()
  deleteAllTransactions(pageId)
}

export function handleRefreshFlightsAndTransactions(
  pageId: PageId,
  delink: boolean,
  useLocalTime: boolean,
  normalizePeriod: boolean,
  onSuccess: (flights: Flights, transList: Transactions) => void,
  onFailure: OnFailure
) {
  let newFlights: Flights = null

  refreshFlights(
    pageId,
    {
      useLocalTime: useLocalTime,
      delink: delink,
      normalizePeriod: normalizePeriod
    },
    (flights) => {
      newFlights = flights
      getTransactions(
        pageId,
        (newTransactions) => {
          onSuccess(newFlights, newTransactions)
        },
        onFailure
      )
    },
    onFailure
  )
}

export function handleSaveAndSendTransactions(
  pageId: PageId,
  isFinal: boolean,
  transactions: Transactions,
  season: string,
  useLocalTime: boolean,
  sendAsBlock: boolean,
  siText: string,
  onSuccess: (transactions: Transactions, infoMsg?: string) => void,
  onFailure: OnFailure
) {
  if (!isFinal) {
    handleSubmit()
  }
  saveTransactions(
    pageId,
    {
      season: season,
      isFinal: isFinal,
      transactions: transactions
    },
    () => {
      sendTransactions(
        pageId,
        {
          season: season,
          isFinal: isFinal,
          useLocalTime: useLocalTime,
          handleAsBlock: sendAsBlock,
          siText: isFinal ? siText : ""
        },
        onSuccess,
        onFailure
      )
    },
    onFailure
  )
}

export function handleMakeAddAndSendTransactions(
  pageId: PageId,
  isFinal: boolean,
  flights: Flights,
  season: string,
  useLocalTime: boolean,
  sendAsBlock: boolean,
  siText: string,
  onSuccess: (transactions: Transactions, infoMsg?: string) => void,
  onFailure: OnFailure
) {
  handleSubmit()
  makeAddTransactions(
    pageId,
    {
      season: season,
      flights: flights
    },
    () => {
      sendTransactions(
        pageId,
        {
          season: season,
          isFinal: isFinal,
          useLocalTime: useLocalTime,
          handleAsBlock: sendAsBlock,
          siText: isFinal ? siText : ""
        },
        onSuccess,
        onFailure
      )
    },
    onFailure
  )
}
//#endregion END functions for Transactions

//#region START functions for Download
export function handleDownload(
  pageId: PageId,
  parameters: IFieldValues,
  isCountReportPage: boolean,
  reportFormatVal: string,
  useLocalTime: boolean,
  separateFiles: boolean,
  separateDays: boolean,
  reportParameters: any,
  onSuccess: (reports: FlightReports) => void,
  onFailure: () => void
) {
  handleSubmit()
  SubmitSelection(
    store.dispatch,
    parameters,
    pageId,
    () => {
      isCountReportPage
        ? getFlightCountReport(pageId, reportParameters, onSuccess, onFailure)
        : handleGetFlightReport(
            pageId,
            getFlightReportParams(
              pageId,
              reportFormatVal,
              parameters,
              useLocalTime,
              separateFiles,
              separateDays
            ),
            onSuccess,
            onFailure
          )
    },
    () => {
      onFailure()
    }
  )
}

function getFlightReportParams(
  pageId: PageId,
  reportFormatVal: string,
  parameters: any,
  useLocalTime: boolean,
  separateFiles: boolean,
  separateDays: boolean
) {
  const pageConfig = getPageConfig[pageId]
  let reportParameters: IFieldValues = {
    reportFormat: reportFormatVal,
    useLocalTime,
    separateFiles,
    separateDays
  }

  if (!pageConfig.flightSelectorConfig.delink) {
    reportParameters.delink = parameters.delink ?? false
  }

  if (!pageConfig.flightSelectorConfig.normalizePeriod) {
    reportParameters.normalizePeriod = parameters.normalizePeriod ?? false
  }

  if (!pageConfig.flightSelectorConfig.canxSinceDate) {
    reportParameters.canxSinceDate = parameters.canxSinceDate ?? ""
  }

  return reportParameters
}

export function handleGetFlightReport(
  pageId: PageId,
  parameters: any,
  onSuccess: (reports: FlightReports) => void,
  onFailure: () => void
) {
  handleSubmit()

  getFlightReports(
    pageId,
    parameters,
    (r: FlightReports) => {
      onSuccess(r)
    },
    onFailure
  )
}
//#endregion END functions for Download

//#region START functions for flights
export function handleSearchFlights(
  pageId: string,
  parameters: any,
  useLocalTime: boolean,
  onSuccess: (flights: Flights) => void,
  onFailure: () => void
) {
  handleSubmit()
  SubmitSelection(
    store.dispatch,
    parameters,
    pageId,
    () => {
      getFlights(
        pageId,
        {
          useLocalTime: useLocalTime,
          delink: parameters.delink,
          normalizePeriod: parameters.normalizePeriod
        },
        onSuccess,
        onFailure
      )
    },
    () => {
      onFailure()
    }
  )
}

export function handleRefreshFlights(
  pageId: string,
  parameters: any,
  onSuccess: (flights: Flights) => void,
  onFailure: () => void
) {
  handleSubmit()
  getFlights(pageId, parameters, onSuccess, onFailure)
}
//#endregion END functions for flights

//#region START functions for availability
export function handleGetFlightAvailability(
  pageId: string,
  parameters: any,
  onArrMatrix: (matrix: string) => void,
  onDepMatrix: (matrix: string) => void,
  onFailure: () => void
) {
  handleSubmit()
  getFlightAvailability(pageId, parameters, onArrMatrix, onDepMatrix, onFailure)
}

export function handleGetSlotAvailability(
  pageId: PageId,
  parameters: any,
  onSuccess: (matrix: string) => void,
  onFailure: () => void
) {
  handleSubmit()
  getSlotAvailability(pageId, parameters, onSuccess, onFailure)
}
//#endregion END functions for availability
